'use client';

import { c as _c } from "react/compiler-runtime";
import { useTurnkey } from '@turnkey/react-wallet-kit';
import { useCallback } from 'react';
import { OauthConnectStash } from './oauth-connect-stash.js';
const POPUP_BLOCKED_REGEX = /failed to open .*login window/i;
const CANCEL_REGEX = /cancel|closed|rejected|user denied/i;
function defaultReturnTo() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search;
}
/**
 * Pre-detect browsers known to block popups. Avoids the wasted try-popup
 * round trip on Safari/mobile (~250ms of crypto + PKCE work that always
 * fails). Mirrors the detection in turnkey-provider's
 * `shouldDefaultToOauthRedirect`. Kept duplicated rather than imported
 * to keep this hook self-contained for tests.
 */
function shouldSkipPopupAttempt() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
  const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
  return isSafari || isMobile;
}
/** The Turnkey kit's rejection when `window.open` returns null (popup blocked). */
// ALLOW-UNKNOWN: type-guard helpers that narrow caught exception values
export function isPopupBlockedError(err) {
  if (!(err instanceof Error)) return false;
  return POPUP_BLOCKED_REGEX.test(err.message);
}
// ALLOW-UNKNOWN: type-guard helpers that narrow caught exception values
function isCancellationError(err) {
  if (!(err instanceof Error)) return false;
  return CANCEL_REGEX.test(err.message);
}
export function useOauthConnect() {
  const $ = _c(7);
  const {
    handleGoogleOauth,
    handleAppleOauth,
    handleXOauth,
    handleAddOauthProvider
  } = useTurnkey();
  let t0;
  if ($[0] !== handleAddOauthProvider || $[1] !== handleAppleOauth || $[2] !== handleGoogleOauth || $[3] !== handleXOauth) {
    t0 = async args => {
      const returnTo = args.returnTo ?? defaultReturnTo();
      let handler = null;
      if (args.provider === "google") {
        handler = handleGoogleOauth;
      } else {
        if (args.provider === "apple") {
          handler = handleAppleOauth;
        } else {
          if (args.provider === "x") {
            handler = handleXOauth;
          }
        }
      }
      if (handler === null) {
        return {
          kind: "error",
          error: new Error(`Unsupported OAuth provider: ${args.provider}`)
        };
      }
      if (shouldSkipPopupAttempt()) {
        OauthConnectStash.write({
          provider: args.provider,
          returnTo
        });
        if (args.provider === "x") {
          ;
          try {
            await handler({
              openInPage: true
            });
            return {
              kind: "redirecting"
            };
          } catch (t1) {
            const redirectErr = t1;
            OauthConnectStash.clear();
            const error = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
            return {
              kind: "error",
              error
            };
          }
        }
        ;
        try {
          const addHandler = handleAddOauthProvider;
          await addHandler({
            providerName: args.provider,
            openInPage: true,
            secondaryClientIds: []
          });
          return {
            kind: "redirecting"
          };
        } catch (t2) {
          const redirectErr_0 = t2;
          OauthConnectStash.clear();
          const error_0 = redirectErr_0 instanceof Error ? redirectErr_0 : new Error(String(redirectErr_0));
          return {
            kind: "error",
            error: error_0
          };
        }
      }
      let capturedToken = null;
      let capturedProviderName = null;
      let capturedPublicKey = null;
      ;
      try {
        await handler({
          openInPage: false,
          onOauthSuccess: params => {
            capturedToken = params.oidcToken;
            capturedProviderName = params.providerName;
            capturedPublicKey = params.publicKey;
          }
        });
      } catch (t3) {
        const err = t3;
        if (isPopupBlockedError(err)) {
          OauthConnectStash.write({
            provider: args.provider,
            returnTo
          });
          if (args.provider === "x") {
            ;
            try {
              await handler({
                openInPage: true
              });
              return {
                kind: "redirecting"
              };
            } catch (t4) {
              const redirectErr_1 = t4;
              OauthConnectStash.clear();
              const error_1 = redirectErr_1 instanceof Error ? redirectErr_1 : new Error(String(redirectErr_1));
              return {
                kind: "error",
                error: error_1
              };
            }
          }
          ;
          try {
            const addHandler_0 = handleAddOauthProvider;
            await addHandler_0({
              providerName: args.provider,
              openInPage: true,
              secondaryClientIds: []
            });
            return {
              kind: "redirecting"
            };
          } catch (t5) {
            const redirectErr_2 = t5;
            OauthConnectStash.clear();
            const error_2 = redirectErr_2 instanceof Error ? redirectErr_2 : new Error(String(redirectErr_2));
            return {
              kind: "error",
              error: error_2
            };
          }
        }
        if (isCancellationError(err)) {
          return {
            kind: "cancelled"
          };
        }
        const error_3 = err instanceof Error ? err : new Error(String(err));
        return {
          kind: "error",
          error: error_3
        };
      }
      const finalToken = capturedToken;
      const finalProviderName = capturedProviderName;
      const finalPublicKey = capturedPublicKey;
      if (finalToken === null || finalProviderName === null || finalPublicKey === null) {
        return {
          kind: "cancelled"
        };
      }
      return {
        kind: "popup-success",
        oidcToken: finalToken,
        providerName: finalProviderName,
        publicKey: finalPublicKey
      };
    };
    $[0] = handleAddOauthProvider;
    $[1] = handleAppleOauth;
    $[2] = handleGoogleOauth;
    $[3] = handleXOauth;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const runOauthConnect = t0;
  let t1;
  if ($[5] !== runOauthConnect) {
    t1 = {
      runOauthConnect
    };
    $[5] = runOauthConnect;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}